'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { resolveRouteTitle } from '@/lib/a11y/pageTitles';
import { getPageTitleOverride, TITLE_OVERRIDE_EVENT } from '@/lib/a11y/usePageTitle';

const SUFFIX = ' · EngageIQ';

// The title this component last wrote, and the last one Next wrote. React only rewrites <title> when its text
// changes, so a route with the same Next title as the previous one (a section index after a sub-page, or an
// override released) would otherwise keep our stale title: we put Next's back instead.
let ownTitle: string | null = null;
let nextTitle: string | null = null;

/**
 * Gives every client page its own <title> (WCAG 2.4.2, Q-2214). Server layouts can only title a whole section;
 * this applies the specific title from lib/a11y/pageTitles on each navigation and keeps it when Next
 * re-renders the section <title> underneath. A `usePageTitle` override (data-dependent titles) wins.
 * Renders nothing; routes without a table entry show the title Next generated.
 */
export function RouteTitle(): null {
  const pathname = usePathname();

  useEffect(() => {
    const wanted = () => {
      const own = getPageTitleOverride();
      if (own) return own;
      const t = resolveRouteTitle(pathname);
      return t ? t + SUFFIX : null;
    };
    const apply = () => {
      if (document.title !== ownTitle) nextTitle = document.title;
      const t = wanted() ?? nextTitle;
      ownTitle = t === nextTitle ? null : t;
      if (t !== null && document.title !== t) document.title = t;
    };
    apply();
    // the whole <head>: React may replace the <title> element rather than its text
    const observer = new MutationObserver(apply);
    observer.observe(document.head, { childList: true, characterData: true, subtree: true });
    window.addEventListener(TITLE_OVERRIDE_EVENT, apply);
    return () => {
      observer.disconnect();
      window.removeEventListener(TITLE_OVERRIDE_EVENT, apply);
    };
  }, [pathname]);

  return null;
}
